import logo from "./roll50-logo.png";
import { useState, useEffect } from "react";
import "./styles.css";

// Simple UI components
const Button = ({ children, onClick, active }) => (
  <button
    onClick={onClick}
    style={{
      background: active ? "#1db954" : "#000",
      color: "#fff",
      padding: "14px",
      borderRadius: 12,
      border: "none",
      width: "100%",
      fontSize: 16,
      marginTop: 8,
    }}
  >
    {children}
  </button>
);

const Card = ({ children }) => (
  <div
    style={{
      background: "rgba(0,0,0,0.6)",
      padding: 16,
      borderRadius: 16,
      marginBottom: 12,
    }}
  >
    {children}
  </div>
);

const Input = ({ value, onChange, placeholder }) => (
  <input
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    style={{
      padding: 12,
      borderRadius: 10,
      border: "1px solid rgba(255,255,255,0.2)",
      width: "100%",
      background: "#111",
      color: "#fff",
    }}
  />
);

export default function PuttingApp() {
  const [mode, setMode] = useState("Practice");
  const [points, setPoints] = useState(0);
  const [putts, setPutts] = useState([]);
  const [step, setStep] = useState("distance");
  const [selectedDistance, setSelectedDistance] = useState(null);
  const challengeName = "The Pressure Ladder";
  const [name, setName] = useState("");
  const [editingName, setEditingName] = useState(true);
  const [showGuide, setShowGuide] = useState(false);
  const [challengeScore, setChallengeScore] = useState(0);
  const [challengeActive, setChallengeActive] = useState(false);
  const [challengeHistory, setChallengeHistory] = useState([]);
  const [challengeFinished, setChallengeFinished] = useState(false);
  // Load saved data
  useEffect(() => {
    const savedName = localStorage.getItem("malos_name");
    const savedPutts = JSON.parse(localStorage.getItem("malos_putts") || "[]");
    const savedHistory = JSON.parse(
      localStorage.getItem("roll50_challenge_history") || "[]"
    );

    if (savedName) {
      setName(savedName);
      setEditingName(false);
    }

    if (savedPutts.length) {
      setPutts(savedPutts);
      setPoints(savedPutts.reduce((a, b) => a + b.pts, 0));
    }

    setChallengeHistory(savedHistory);
  }, []);
  // Save data
  useEffect(() => {
    localStorage.setItem("malos_name", name);
    localStorage.setItem("malos_putts", JSON.stringify(putts));
  }, [name, putts]);

  const addPutt = (pts, label) => {
    const newPutts = [...putts, { pts, label }];

    if (newPutts.length > 50) newPutts.shift();

    setPutts(newPutts);

    const newTotal = newPutts.reduce((a, b) => a + b.pts, 0);
    setPoints(newTotal);

    if (mode === "Challenges") {
      setChallengeScore((prev) => (prev ?? 0) + pts);
    }

    setStep("distance");
    setSelectedDistance(null);
  };

  const avg = putts.length ? (points / putts.length).toFixed(2) : 0;

  const last10 = putts.slice(-10);
  const last10Avg = last10.length
    ? last10.reduce((a, b) => a + b.pts, 0) / last10.length
    : 0;

  const trendColor =
    last10Avg >= 1 ? "#1db954" : last10Avg <= -0.5 ? "#ff4d4d" : "#ffaa00";

  const shareScore = async () => {
    const text = `${name || "Player"} – Roll50\nLast 50 Putts: ${avg}`;

    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        alert("Score copied!");
      }
    } catch {
      alert(text);
    }
  };
  const saveChallengeResult = () => {
    const result =
      challengeScore >= 10
        ? "Elite"
        : challengeScore >= 5
        ? "Solid"
        : "Needs Work";

    const newEntry = {
      name: challengeName,
      date: new Date().toLocaleDateString(),
      score: challengeScore,
      avg,
      result,
    };

    const updatedHistory = [newEntry, ...challengeHistory];

    setChallengeHistory(updatedHistory);

    localStorage.setItem(
      "roll50_challenge_history",
      JSON.stringify(updatedHistory)
    );
  };
  const distances = [
    { label: "0–3 ft", key: "0-3" },
    { label: "4–6 ft", key: "4-6" },
    { label: "7–9 ft", key: "7-9" },
    { label: "10–15 ft", key: "10-15" },
    { label: "16–24 ft", key: "16-24" }, // NEW
    { label: "25+ ft", key: "25+" }, // NEW
  ];

  const resultsMap = {
    "0-3": [
      { label: "Make", pts: 1 },
      { label: "Miss", pts: -3 },
    ],
    "4-6": [
      { label: "Make", pts: 2 },
      { label: "Miss", pts: -2 },
    ],
    "7-9": [
      { label: "Make", pts: 3 },
      { label: "2-putt", pts: 1 },
      { label: "3-putt", pts: -3 },
    ],
    "10-15": [
      { label: "Make", pts: 4 },
      { label: "2-putt (in)", pts: 2 },
      { label: "2-putt (out)", pts: 0 },
      { label: "3-putt", pts: -2 },
    ],
    "16-24": [
      { label: "Make", pts: 4 },
      { label: "2-putt (in)", pts: 2 },
      { label: "2-putt (out)", pts: 0 },
      { label: "3-putt", pts: -2 },
    ],
    "25+": [
      { label: "Make", pts: 6 }, // 🔥 boosted
      { label: "2-putt (in)", pts: 3 },
      { label: "2-putt (out)", pts: 1 },
      { label: "3-putt", pts: -2 },
    ],
  };

  return (
    <div style={{ padding: 16, minHeight: "100vh" }}>
      {/* Logo */}
      <div
        style={{
          textAlign: "center",
          marginBottom: 8,
          marginTop: -10,
        }}
      >
        <img
          src={logo}
          alt="Malos Logo"
          style={{
            width: 180,
            maxWidth: "80%",
            objectFit: "contain",
          }}
        />
      </div>
      {/* HERO (add this) */}
      {/* HERO */}
      <div
        style={{
          textAlign: "center",
          marginBottom: 18,
          padding: "12px 16px",
          borderRadius: 16,
          background: "rgba(255,255,255,0.03)",
        }}
      >
        <h1
          style={{
            fontSize: 20,
            marginBottom: 10,
            lineHeight: 1.3,
          }}
        >
          Tired of blowing holes on the green?
        </h1>

        <p
          style={{
            fontSize: 15,
            marginBottom: 10,
            fontWeight: 500,
          }}
        >
          Track it. Fix it. Lower your scores.
        </p>
      </div>
      {/* Name */}
      <Card>
        {editingName ? (
          <>
            <Input
              placeholder="Enter your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Button onClick={() => setEditingName(false)}>Save</Button>
          </>
        ) : (
          <>
            <h3>{name}'s Roll50</h3>
            <Button onClick={() => setEditingName(true)}>Edit Name</Button>
          </>
        )}
      </Card>
      {/* Average */}
      <Card>
        <div style={{ textAlign: "center" }}>
          <p style={{ opacity: 0.7 }}>Average</p>

          <h1 style={{ fontSize: 42, margin: 0, color: trendColor }}>{avg}</h1>

          <p style={{ fontSize: 12, color: trendColor }}>
            {avg >= 2
              ? "Elite"
              : avg >= 1
              ? "Solid"
              : avg >= 0
              ? "Average"
              : "Needs Work"}
          </p>

          <p style={{ opacity: 0.7 }}>{putts.length}/50 putts</p>

          <p style={{ fontSize: 12, opacity: 0.5 }}>Total Points: {points}</p>
        </div>
      </Card>
      <Card>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr",
            gap: 8,
          }}
        >
          <Button
            onClick={() => setMode("Practice")}
            active={mode === "Practice"}
          >
            Practice
          </Button>
          <Button onClick={() => setMode("Course")} active={mode === "Course"}>
            Course
          </Button>
          <Button
            onClick={() => setMode("Challenges")}
            active={mode === "Challenges"}
          >
            Challenges
          </Button>
          <Button
            onClick={() => setMode("Profile")}
            active={mode === "Profile"}
          >
            Profile
          </Button>{" "}
        </div>
      </Card>
      {/* Challenges Mode */}
      {mode === "Challenges" && (
        <Card>
          <h2 style={{ marginBottom: 8 }}>{challengeName}</h2>
          <p style={{ opacity: 0.7, marginBottom: 16 }}>
            One putt from each distance. Finish every hole completely.
          </p>
          {challengeActive && (
            <p style={{ color: "#1db954", fontWeight: 600, marginBottom: 10 }}>
              Challenge running — track your putts
            </p>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 16,
            }}
          >
            {["3 ft", "6 ft", "9 ft", "15 ft", "24 ft", "31 ft"].map(
              (distance) => (
                <div
                  key={distance}
                  style={{
                    background: "#111",
                    padding: 14,
                    borderRadius: 12,
                    textAlign: "center",
                  }}
                >
                  <strong>{distance}</strong>

                  <p style={{ opacity: 0.6, fontSize: 12 }}>Play it out</p>
                </div>
              )
            )}
          </div>
          <div
            style={{
              marginTop: 16,
              padding: 12,
              background: "#111",
              borderRadius: 12,
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            <strong>Scoring:</strong>
            <br />
            Every putt follows standard Roll50 scoring. Finish every hole
            completely.
          </div>
          <Button
            onClick={() => {
              setMode("Challenges");
              setChallengeScore(0);
              setPutts([]);
              setPoints(0);
              setStep("distance");
              setSelectedDistance(null);
              setChallengeActive(true);
              setChallengeFinished(false);
            }}
            active={challengeActive}
          >
            {challengeActive ? "Challenge Active" : "Start Challenge"}
          </Button>
          {challengeActive && !challengeFinished && (
            <Button
              onClick={() => {
                setChallengeActive(false);
                setChallengeFinished(true);
                saveChallengeResult();
              }}
            >
              Finish Challenge
            </Button>
          )}

          {challengeFinished && (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: "#111",
                borderRadius: 12,
              }}
            >
              <h3 style={{ marginBottom: 10 }}>Challenge Complete</h3>

              <p style={{ opacity: 0.8 }}>
                Total Points: <strong>{challengeScore}</strong>
              </p>

              <p style={{ opacity: 0.8 }}>
                Average: <strong>{avg}</strong>
              </p>

              <p style={{ marginTop: 10, color: "#1db954", fontWeight: 600 }}>
                {challengeScore >= 10
                  ? "Elite Round"
                  : challengeScore >= 5
                  ? "Solid Round"
                  : "Needs Work"}
              </p>
            </div>
          )}
        </Card>
      )}{" "}
      {mode === "Profile" && (
        <Card>
          <h2 style={{ marginBottom: 10 }}>Profile</h2>

          <p style={{ opacity: 0.7 }}>Player: {name || "Anonymous"}</p>

          <p style={{ opacity: 0.7 }}>Total Putts: {putts.length}</p>

          <p style={{ opacity: 0.7 }}>Average Score: {avg}</p>

          <h3 style={{ marginTop: 20 }}>Challenge History</h3>

          {challengeHistory.length === 0 ? (
            <p style={{ opacity: 0.6 }}>No completed challenges yet</p>
          ) : (
            challengeHistory.map((c, i) => (
              <div
                key={i}
                style={{
                  background: "#111",
                  padding: 12,
                  borderRadius: 12,
                  marginTop: 10,
                }}
              >
                <strong>{c.name}</strong>

                <p style={{ fontSize: 12, opacity: 0.7 }}>{c.date}</p>

                <p style={{ fontSize: 13 }}>
                  {c.score} pts • Avg {c.avg}
                </p>

                <p
                  style={{
                    color: "#1db954",
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {c.result}
                </p>
              </div>
            ))
          )}
        </Card>
      )}
      {/* Input */}
      <Card>
        {step === "distance" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            {distances.map((d) => (
              <button
                key={d.key}
                onClick={() => {
                  setSelectedDistance(d.key);
                  setStep("result");
                }}
                style={{
                  background: "#fff",
                  color: "#000",
                  padding: "14px",
                  borderRadius: 12,
                  border: "none",
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                {d.label}
              </button>
            ))}
          </div>
        )}
        {step === "result" &&
          resultsMap[selectedDistance].map((r, i) => (
            <Button
              key={i}
              onClick={() => addPutt(r.pts, `${selectedDistance} ${r.label}`)}
            >
              {r.label}
            </Button>
          ))}

        {step === "result" && (
          <Button onClick={() => setStep("distance")}>Back</Button>
        )}
      </Card>
      {/* Actions */}
      <Card>
        <Button onClick={shareScore}>Share Score</Button>
        <Button
          onClick={() => {
            setPutts([]);
            setPoints(0);
          }}
        >
          Reset Session
        </Button>
      </Card>
      {/* Collapsible Guide */}
      <Card>
        {/* Top Content */}
        <div style={{ textAlign: "center" }}>
          <h3>How it works</h3>

          <p style={{ fontSize: 14, opacity: 0.8, lineHeight: 1.6 }}>
            1. Enter your putts
            <br />
            2. Get your Roll50 score
            <br />
            3. Track trends and improve
          </p>

          <p style={{ fontSize: 12, opacity: 0.6, marginTop: 8 }}>
            Track your last 50 putts. Higher average = better performance.
          </p>
        </div>

        {/* Button */}
        <div style={{ textAlign: "center", marginTop: 10 }}>
          <button
            onClick={() => setShowGuide(!showGuide)}
            style={{
              background: "none",
              border: "none",
              color: "#1db954",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {showGuide ? "Hide scoring details ▲" : "See how scoring works ▼"}
          </button>
        </div>

        {/* Guide */}
        {showGuide && (
          <div
            style={{
              marginTop: 12,
              fontSize: 13,
              lineHeight: 1.6,
              color: "#fff",
            }}
          >
            <hr style={{ opacity: 0.2, marginBottom: 10 }} />

            <p>
              <strong>0–3 ft</strong> → Make +1 | Miss -3
            </p>
            <p>
              <strong>4–6 ft</strong> → Make +2 | Miss -2
            </p>
            <p>
              <strong>7–9 ft</strong> → Make +3 | 2-putt +1 | 3-putt -3
            </p>

            <p>
              <strong>10–15 ft</strong> → Make +4 | 2-putt (in ≤3 ft) +2 |
              2-putt (out &gt;3 ft) 0 | 3-putt -2
            </p>

            <p>
              <strong>16–24 ft</strong> → Make +4 | 2-putt (in ≤3 ft) +2 |
              2-putt (out &gt;3 ft) 0 | 3-putt -2
            </p>

            <p>
              <strong>25+ ft</strong> → Make +6 | 2-putt (in ≤3 ft) +3 | 2-putt
              (out &gt;3 ft) +1 | 3-putt -2
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
